import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "./ReviewsComponent.scss";
import WriteReviewComponent from "./WriteReviewComponent";
import CustomRating from "../../../common/components/rating-component/rating-component";
import Avatar from "../../../assets/icons/profile-Icon.svg";
import { getCurrentUserDetails } from "../../../common/routines/user-operations";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ActionsMenu from "../../../common/components/listing-table/actions-menu";
import { REVIEW_ACTION } from "../../provider-section/service-management/services-list/provider-services-list.helper";
import DeleteReviewPopup from "./DeleteReviewPopup";
import { ActionOption } from "../../../common/components/listing-table/listing-table-props";
import { deleteReview } from "../../../api/details-page.api";
import { HttpStatusCode } from "axios";
import { AlertComponent } from "../../../common/components";
import EditReviewComponent from "./EditReviewComponent";
import { REVIEW_API_ACTION } from "../../../utils/constants";

interface RootState {
  session: {
    isLoggedIn: boolean;
    user: {
      id: number;
    };
  };
}

interface Review {
  id: number;
  user: {
    id: number;
    first_name: string;
    last_name?: string;
  };
  createdAt: string;
  description: string;
  rating: number;
}

const ReviewsComponent: React.FC<{
  reviews: Review[];
  totalReviews: number;
  rating: number;
  isMobile?: boolean;
  partnerId: number;
  onReviewSubmitted?: () => void;
  onReviewDeleted: () => void;
  fromReviewsPage?: boolean;
}> = ({
  reviews,
  totalReviews,
  rating,
  isMobile,
  partnerId,
  onReviewSubmitted,
  onReviewDeleted,
  fromReviewsPage,
}) => {
  const [showWriteReview, setShowWriteReview] = useState(false);
  const { isLoggedIn } = useSelector((state: RootState) => state.session);
  const userDetails = getCurrentUserDetails("user");
  const userId = userDetails?.user?.id ?? userDetails?.id;
  const hasUserReviewed = reviews.some((review) => review.user.id === userId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const shareRef = useRef<HTMLDivElement>(null);
  const [selectedReview, setSelectedReview] = useState<any | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [showEditReview, setShowEditReview] = useState<boolean>(false);

  const onDeletePopupClose = () => {
    setOpenDeletePopup(false);
    setSelectedReview(null);
  };
  const deleteUserReview = async () => {
    setOpenDeletePopup(false);
    try {
      const response = await deleteReview(partnerId, {
        reviewId: selectedReview?.id,
      });
      if (response?.status === HttpStatusCode.Ok && response?.data?.success) {
        setAlertType("success");
        onReviewDeleted();
      } else {
        setAlertType("error");
      }
      setAlertMessage(response?.data?.data?.message);
      setAlertOpen(true);
    } catch (err: any) {
      setAlertMessage(err?.response?.data.message);
      setAlertType("error");
      setAlertOpen(true);
    }
    setShowEditReview(false);
  };
  const handleMenuOptionClick = (option: ActionOption) => {
    if (option.value === "delete") {
      setOpenDeletePopup(true);
    } else if (option.value === "edit") {
      setShowEditReview(true);
    }
    handleClose();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWriteReviewClick = () => {
    setSelectedReview(null);
    setShowWriteReview(true);
  };

  const handleCloseWriteReview = () => {
    setShowWriteReview(false);
    setSelectedReview(null);
  };

  const handleReviewSubmitSuccess = () => {
    setShowWriteReview(false);
    setSelectedReview(null);
    if (onReviewSubmitted) {
      onReviewSubmitted();
      setAlertMessage("Review created successfully.");
      setAlertType("success");
      setAlertOpen(true);
    }
  };

  const onSubmitFailed = () => {
    if (showEditReview) {
      setAlertMessage("Unable to update the review. Please try again.");
    } else {
      setAlertMessage("Unable to create the review. Please try again.");
    }
    setAlertType("error");
    setAlertOpen(true);
  };
  const handleReviewUpdateSuccess = () => {
    setShowEditReview(false);
    setSelectedReview(null);
    if (onReviewSubmitted) {
      onReviewSubmitted();
      setAlertMessage("Review updated successfully.");
      setAlertType("success");
      setAlertOpen(true);
    }
  };

  const getReviewAction = (review: any) => {
    return (
      <Box>
        <CustomRating value={review?.rating} />
        {review.userId === userId && (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <IconButton
              sx={{ marginRight: "-13px" }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedReview(review);
                handleMenuClick(e, null);
              }}
              className="vertical-ellipse-icon"
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };
  return (
    <>
      {!isMobile ? (
        <Box className="provider-reviews-section">
          <Box className="reviews-header">
            <Box>
              <Typography className="reviews-title">
                {fromReviewsPage ? "All Customer Reviews" : "Customer Reviews"}
              </Typography>
              <CustomRating
                showRating={true}
                value={rating}
                totalReviews={totalReviews}
              />
            </Box>
            {isLoggedIn && !hasUserReviewed && (
              <button
                className="write-review-btn"
                onClick={handleWriteReviewClick}
              >
                Write a Review
              </button>
            )}
          </Box>
          {showWriteReview && (
            <Box ref={shareRef}>
              <WriteReviewComponent
                onClose={handleCloseWriteReview}
                partnerId={partnerId}
                onSubmitSuccess={handleReviewSubmitSuccess}
                onSubmitFailed={onSubmitFailed}
              />
            </Box>
          )}
          <Box className="reviews-list">
            {reviews.map((review: any) => (
              <>
                {(review?.userId !== userId || !showEditReview) && (
                  <Box key={review.id} className="review-item">
                    <Box sx={{ display: "flex", gap: "16px" }}>
                      <Box className="reviewer-avatar">
                        <img src={Avatar} alt="avatar" />
                      </Box>
                      <Box className="review-item-body">
                        <Box className="review-item-header">
                          <Typography className="review-author">
                            {review?.user?.first_name || review?.user?.last_name
                              ? `${review.user.first_name || ""} ${review.user.last_name || ""}`.trim()
                              : "Unknown User"}
                          </Typography>
                          <Typography className="review-date">
                            {new Date(review.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              },
                            )}
                          </Typography>
                        </Box>
                        {/* <h3 className="review-item-title">{review.title}</h3> */}
                        <Typography
                          className="review-item-description"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {review.description}
                        </Typography>
                      </Box>
                    </Box>
                    {getReviewAction(review)}
                  </Box>
                )}
                {review?.userId === userId && showEditReview && (
                  <EditReviewComponent
                    selectedReview={review}
                    onClose={() => {
                      setShowEditReview(false);
                    }}
                    partnerId={partnerId}
                    onSubmitSuccess={handleReviewUpdateSuccess}
                  />
                )}
              </>
            ))}
          </Box>
        </Box>
      ) : (
        <Box className="provider-reviews-section-mobile">
          <Box className="reviews-header">
            <Box>
              <Typography className="reviews-title">
                Customer Reviews
              </Typography>
              <CustomRating
                showRating={true}
                value={rating}
                totalReviews={totalReviews}
              />
            </Box>
            {isLoggedIn && !hasUserReviewed && (
              <button
                className="write-review-btn"
                onClick={handleWriteReviewClick}
              >
                Write a Review
              </button>
            )}
          </Box>
          {showWriteReview && (
            <WriteReviewComponent
              onClose={handleCloseWriteReview}
              partnerId={partnerId}
              onSubmitSuccess={handleReviewSubmitSuccess}
              onSubmitFailed={onSubmitFailed}
            />
          )}
          <Box className="reviews-list">
            {reviews.map((review) => (
              <>
                {(review?.userId !== userId || !showEditReview) && (
                  <Box key={review.id} className="review-item">
                    <Box sx={{ display: "flex", gap: "16px" }}>
                      <Box className="reviewer-avatar">
                        <img src={Avatar} alt="avatar" />
                      </Box>
                      <Box className="review-item-body">
                        <Box className="review-item-header">
                          <Typography className="review-author">{`${review.user.first_name} ${
                            review.user.last_name || ""
                          }`}</Typography>
                          <Typography className="review-date">
                            {new Date(review.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              },
                            )}
                          </Typography>
                        </Box>
                        <h3 className="review-item-title">{review.title}</h3>
                        <Typography className="review-item-description">
                          {review.description}
                        </Typography>
                      </Box>
                    </Box>
                    {getReviewAction(review)}
                  </Box>
                )}

                {review?.userId === userId && showEditReview && (
                  <EditReviewComponent
                    selectedReview={review}
                    onClose={() => {
                      setShowEditReview(false);
                    }}
                    partnerId={partnerId}
                    onSubmitSuccess={handleReviewUpdateSuccess}
                  />
                )}
              </>
            ))}
          </Box>
        </Box>
      )}
      <ActionsMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        onOptionClick={handleMenuOptionClick}
        options={REVIEW_ACTION}
        width="150px"
      />
      <DeleteReviewPopup
        open={openDeletePopup}
        onClose={onDeletePopupClose}
        onDelete={deleteUserReview}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default ReviewsComponent;
